import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { icons } from "../../../../../svg";
import FixArrow from "../../../../../svg/fixArrow.svg?react"; // Adjust the import path as necessary
import { useNitroStore } from "../../../../../store/Store";
import IRouteValidation from "../types/IRouteValidation";
import NitroService from "../../../../../utils/nitro.service";
import RouteValidationService from "../lib/service";

interface AddMissingOriginProps {
  selectedOrigin: { id: string; name: string };
  selectedOccupant?: { id: string; name: string };
  validation: IRouteValidation | null | any;
  onChange: () => void;
  onCompleteChange: (response: any) => void;
}

function normalizeProduct(product: string): string {
  if (["AMMONIA", "UREA"].includes(product.toUpperCase())) {
    return product.charAt(0).toUpperCase() + product.slice(1).toLowerCase();
  }
  if (product.toUpperCase() === "SUPERU") {
    return "SuperU";
  }
  return product.toUpperCase();
}
function normalizeMode(mode: string): string {
  return mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase();
}

const AddMissingOrigin: React.FC<AddMissingOriginProps> = ({
  selectedOrigin,
  selectedOccupant,
  validation,
  onChange,
  onCompleteChange,
}) => {
  const [
    openInsightsPanel,
    insightsEditing,
    currentFilters,
    selectedLocation,
    selectedProduct,
    selectedMode,
  ] = useNitroStore((state) => [
    state.openInsightsPanel,
    state.insightsEditing,
    state.currentFilters,
    state.selectedLocation,
    state.product,
    state.mode,
  ]);
  const product = normalizeProduct(selectedProduct);
  const mode = normalizeMode(selectedMode);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  // origin state handling
  const [originCompeteStateUpdated, setOriginCompeteStateUpdated] =
    useState(false);
  useEffect(() => {
    const checkOriginStateUpdate = async () => {
      // check if the state is already updated or if validation allows it to be updated
      if (originCompeteStateUpdated || validation?.inStateTheyCompeteIn) {
        return;
      }

      // get the location details for the selected origin
      const record = await NitroService.getLocationDetails(selectedOrigin.id);
      const occupant = record?.occupants?.find(
        (o) => o.id === selectedOccupant?.id
      );

      // check if the backend updated the occupant's states
      if (
        occupant &&
        selectedLocation &&
        occupant.states.includes(selectedLocation.state)
      ) {
        setOriginCompeteStateUpdated(true);
      }
    };
    checkOriginStateUpdate();
  }, [
    insightsEditing,
    originCompeteStateUpdated,
    selectedOrigin,
    selectedOccupant?.id,
    validation,
    selectedOccupant,
    selectedLocation?.state,
    selectedLocation,
  ]);
  // origin product handling
  const [originProductUpdated, setOriginProductUpdated] = useState(false);
  useEffect(() => {
    const checkOriginProductUpdate = async () => {
      // check if the product is already updated or if validation allows it to be updated
      if (originProductUpdated || validation?.originCanStoreProduct) {
        return;
      }

      // get the location details for the selected origin
      const record = await NitroService.getLocationDetails(selectedOrigin.id);
      const occupant = record?.occupants?.find(
        (o) => o.id === selectedOccupant?.id
      );

      // check if the backend updated the occupant's products
      if (
        occupant &&
        occupant.products.find(
          (p) => p.product.toLowerCase() === selectedProduct.toLowerCase()
        )
      ) {
        setOriginProductUpdated(true);
      }
    };
    checkOriginProductUpdate();
  }, [
    originProductUpdated,
    selectedOrigin?.id,
    selectedOccupant?.id,
    selectedProduct,
    validation,
  ]);
  // origin mode handling
  const [originModeUpdated, setOriginModeUpdated] = useState(false);
  useEffect(() => {
    const checkOriginModeUpdate = async () => {
      // check if the mode is already updated or if validation allows it to be updated
      if (originModeUpdated || validation?.hasOutboundMode) {
        return;
      }

      // get the location details for the selected origin
      const record = await NitroService.getLocationDetails(selectedOrigin.id);

      // check if the backend updated the location's outbound modes
      if (record?.outboundModes.includes(selectedMode.toUpperCase())) {
        setOriginModeUpdated(true);
      }
    };
    checkOriginModeUpdate();
  }, [
    originModeUpdated,
    selectedOrigin?.id,
    selectedOccupant?.id,
    selectedMode,
    validation,
  ]);
  // origin serving carrier handling
  const [originServingCarrierUpdated, setOriginServingCarrierUpdated] =
    useState(false);
  useEffect(() => {
    const checkOriginServingCarrierUpdate = async () => {
      // check if the serving carrier is already updated or if validation allows it to be updated
      if (
        originServingCarrierUpdated ||
        !validation?.hasRailRouteFromOrigin ||
        validation?.hasRailRouteFromServingOriginCarrier
      ) {
        return;
      }

      // get the location details for the selected origin
      const record = await NitroService.getLocationDetails(selectedOrigin.id);
      const carriers = record?.carriers || [];

      // check if the backend updated the location's serving carrier
      if (carriers.length > 0) {
        setOriginServingCarrierUpdated(true);
      }
    };
    checkOriginServingCarrierUpdate();
  }, [originServingCarrierUpdated, selectedOrigin?.id, validation]);
  // destination product handling
  const [destinationProductUpdated, setDestinationProductUpdated] =
    useState(false);
  useEffect(() => {
    const checkDestinationProductUpdate = async () => {
      // check if the destination product is already updated or if validation allows it to be updated
      if (destinationProductUpdated || validation?.destinationCanStoreProduct) {
        return;
      }

      // get the location details for the selected destination
      if (!selectedLocation) return;
      const record = await NitroService.getLocationDetails(selectedLocation.id);

      // check if the backend updated the location's products
      // look thru occupants to see if the product is already added to any of them
      const isProductAdded = record?.occupants?.map((o) => {
        const products = o.products.map((p) => p.product.toLowerCase());
        return products.includes(selectedProduct.toLowerCase());
      });
      if (isProductAdded?.includes(true)) {
        setDestinationProductUpdated(true);
      }
    };
    checkDestinationProductUpdate();
  }, [
    destinationProductUpdated,
    selectedLocation,
    selectedLocation?.id,
    selectedOccupant?.id,
    selectedProduct,
    validation,
  ]);
  // destination mode handling
  const [destinationModeUpdated, setDestinationModeUpdated] = useState(false);
  useEffect(() => {
    const checkDestinationModeUpdate = async () => {
      // check if the mode is already updated or if validation allows it to be updated
      if (destinationModeUpdated || validation?.hasInboundMode) {
        return;
      }

      // get the location details for the selected destination
      if (!selectedLocation) return;
      const record = await NitroService.getLocationDetails(selectedLocation.id);

      // check if the backend updated the location's inbound modes
      if (record?.inboundModes.includes(selectedMode.toUpperCase())) {
        setDestinationModeUpdated(true);
      }
    };
    checkDestinationModeUpdate();
  }, [
    destinationModeUpdated,
    selectedLocation?.id,
    selectedOccupant?.id,
    selectedMode,
    validation,
    selectedLocation,
  ]);
  // destination serving carrier handling
  const [
    destinationServingCarrierUpdated,
    setDestinationServingCarrierUpdated,
  ] = useState(false);
  useEffect(() => {
    const checkServingCarrierUpdate = async () => {
      // check if the serving carrier is already updated or if validation allows it to be updated
      if (
        destinationServingCarrierUpdated ||
        !validation?.hasRailRouteToDestination ||
        validation?.hasRailRouteToServingDestinationCarrier
      ) {
        return;
      }

      // get the location details for the selected destination
      const record = await NitroService.getLocationDetails(
        selectedLocation?.id ?? ""
      );
      const carriers = record?.carriers || [];

      // check if the backend updated the location's serving carrier
      if (carriers.length > 0) {
        setDestinationServingCarrierUpdated(true);
      }
    };
    checkServingCarrierUpdate();
  }, [destinationServingCarrierUpdated, selectedLocation?.id, validation]);
  function calculateTasksFinished() {
    let total = 0;
    let completed = 0;
    if (!validation) {
      return { total: 0, completed: 0 };
    }
    if (!validation.inStateTheyCompeteIn) {
      total += 1;
      if (originCompeteStateUpdated) {
        completed += 1;
      }
    }
    if (!validation.originCanStoreProduct) {
      total += 1;
      if (originProductUpdated) {
        completed += 1;
      }
    }
    if (!validation.hasOutboundMode) {
      total += 1;
      if (originModeUpdated) {
        completed += 1;
      }
    }
    if (
      selectedMode.toLowerCase() === "rail" &&
      validation.hasRailRouteFromOrigin &&
      !validation.hasRailRouteFromServingOriginCarrier
    ) {
      total += 1;
      if (originServingCarrierUpdated) {
        completed += 1;
      }
    }
    if (!validation.destinationCanStoreProduct) {
      total += 1;
      if (destinationProductUpdated) {
        completed += 1;
      }
    }
    if (!validation.hasInboundMode) {
      total += 1;
      if (destinationModeUpdated) {
        completed += 1;
      }
    }
    if (
      selectedMode.toLowerCase() === "rail" &&
      validation.hasRailRouteToDestination &&
      !validation.hasRailRouteToServingDestinationCarrier
    ) {
      total += 1;
      if (destinationServingCarrierUpdated) {
        completed += 1;
      }
    }
    return { total, completed };
  }
  const { total, completed } = calculateTasksFinished();
  const tasksFinished = total === completed;

  const onComplete = async () => {
    try {
      const result = await RouteValidationService.validate({
        mode: selectedMode,
        product: selectedProduct,
        originLocationId: selectedOrigin.id,
        originOccupantId: selectedOccupant?.id || "",
        destinationId: selectedLocation?.id || "",
        shipDate: currentFilters.month,
        isCity: !!selectedLocation?.city,
      });
      onCompleteChange(result);
      setIsComplete(true);
    } catch (error) {
      console.error("API call failed:", error);
    }
  };
  return (
    <div className="add-missing-origin-container">
      {/* Selected Origin Box */}
      <div
        style={{
          height: selectedOccupant?.name ? "90px" : "70px",
        }}
        className="selected-origin-container"
      >
        <div
          style={{
            color: isComplete ? "#4B4B4B" : "black",
          }}
          className="selected-origin-text"
        >
          Selected Origin
        </div>
        <div className="selected-origin-details-container">
          <div
            style={{
              bottom: selectedOccupant?.name ? "30px" : "10px",
            }}
            className="selected-origin-name"
          >
            {selectedOrigin.name}
          </div>
          <Button
            id="change-origin-button"
            variant="text"
            onClick={onChange}
            disabled={isComplete}
            style={{
              color: isComplete ? "#4B4B4B" : "#005CA6",
              bottom: selectedOccupant?.name ? "23px" : "3px",
              display: "flex",
              position: "absolute",
              right: "15px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            Change
          </Button>
        </div>
        <div className="selected-origin-occupant">{selectedOccupant?.name}</div>
      </div>

      {/* Missing Tasks Section */}
      <div className="missing-tasks-container">
        <span className="missing-tasks-header">Missing Tasks</span>
        <span className="missing-tasks-value">
          {completed} of {total} Completed
        </span>
      </div>

      {/* Task List */}
      <div className="task-list-container">
        {/* Origin State */}
        {!validation?.inStateTheyCompeteIn && (
          <div className="task-container">
            <div
              style={{
                height: originCompeteStateUpdated ? "140px" : "200px",
                boxShadow: originCompeteStateUpdated
                  ? "-2px 0px 0px #009F5C"
                  : "-2px 0px 0px #FF0000",
              }}
              className="task-box"
            >
              <div className="task-title-row">
                <img
                  src={
                    originCompeteStateUpdated
                      ? icons.TaskCompleted
                      : icons.TodoMarker
                  }
                  alt="Todo Marker"
                />
                <span className="task-title">
                  Add {selectedLocation?.state} as a Possible Destination State
                </span>
              </div>
              <div className="task-details-row">
                <div className="task-details-first-point">
                  1. {selectedLocation?.state} needs to be added as a
                  destination state this origin competes in.
                </div>
                <div className="task-details-second-point">
                  2. Locate the selected occupant and update the dropdown menu
                  labeled “States They Compete In”
                </div>
              </div>
              {!originCompeteStateUpdated && (
                <div className="fix-issue-button-row">
                  <Button
                    id="fix-issue-button"
                    variant="contained"
                    size="medium"
                    color="primary"
                    endIcon={<FixArrow />}
                    onClick={() => {
                      openInsightsPanel(selectedOrigin.id, "Occupants", true);
                    }}
                  >
                    Fix Issue: Add State
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Origin Product */}
        {!validation?.originCanStoreProduct && (
          <div className="task-container">
            <div
              style={{
                height: originProductUpdated ? "140px" : "200px",
                boxShadow: originProductUpdated
                  ? "-2px 0px 0px #009F5C"
                  : "-2px 0px 0px #FF0000",
              }}
              className="task-box"
            >
              <div className="task-title-row">
                <img
                  src={
                    originProductUpdated
                      ? icons.TaskCompleted
                      : icons.TodoMarker
                  }
                  alt="Todo Marker"
                />
                <span className="task-title">
                  Add {product} to Origin&apos;s Product List
                </span>
              </div>
              <div className="task-details-row">
                <div className="task-details-first-point">
                  1. This origin has to have this product added to its product
                  list.
                </div>
                <div className="task-details-second-point">
                  2. Locate the selected occupant and update the product list
                  associated with this origin.
                </div>
              </div>
              {!originProductUpdated && (
                <div className="fix-issue-button-row">
                  <Button
                    id="fix-issue-button"
                    variant="contained"
                    size="medium"
                    color="primary"
                    endIcon={<FixArrow />}
                    onClick={() => {
                      openInsightsPanel(selectedOrigin.id, "Occupants", true);
                    }}
                  >
                    Fix Issue: Add Product
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Origin Mode */}
        {!validation?.hasOutboundMode && (
          <div className="task-container">
            <div
              style={{
                height: originModeUpdated ? "140px" : "200px",
                boxShadow: originModeUpdated
                  ? "-2px 0px 0px #009F5C"
                  : "-2px 0px 0px #FF0000",
              }}
              className="task-box"
            >
              <div className="task-title-row">
                <img
                  src={
                    originModeUpdated ? icons.TaskCompleted : icons.TodoMarker
                  }
                  alt="Todo Marker"
                />
                <span className="task-title">
                  Add Outbound {mode} to Origin
                </span>
              </div>
              <div className="task-details-row">
                <div className="task-details-first-point">
                  1. Outbound {selectedMode.toLocaleLowerCase()} has to be added
                  as a capability.
                </div>
                <div className="task-details-second-point">
                  2. Select the checkbox labeled “outbound” for this origin.
                </div>
              </div>
              {!originModeUpdated && (
                <div className="fix-issue-button-row">
                  <Button
                    id="fix-issue-button"
                    variant="contained"
                    size="medium"
                    color="primary"
                    endIcon={<FixArrow />}
                    onClick={() => {
                      openInsightsPanel(selectedOrigin.id, "Logistics", true);
                    }}
                  >
                    Fix Issue: Add Outbound {mode}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Origin Serving Carrier */}
        {selectedMode.toLowerCase() === "rail" &&
          validation?.hasRailRouteFromOrigin &&
          !validation?.hasRailRouteFromServingOriginCarrier && (
            <div className="task-container">
              <div
                style={{
                  height: originServingCarrierUpdated ? "140px" : "200px",
                  boxShadow: originServingCarrierUpdated
                    ? "-2px 0px 0px #009F5C"
                    : "-2px 0px 0px #FF0000",
                }}
                className="task-box"
              >
                <div className="task-title-row">
                  <img
                    src={
                      originServingCarrierUpdated
                        ? icons.TaskCompleted
                        : icons.TodoMarker
                    }
                    alt="Todo Marker"
                  />
                  <span className="task-title">
                    Add Missing Serving Carrier to Origin
                  </span>
                </div>
                <div className="task-details-row">
                  <div className="task-details-first-point">
                    1. This origin has to have a serving carrier selected.
                  </div>
                  <div className="task-details-second-point">
                    2. Add a carrier to the “Serving and Connecting Carriers”
                    section associated with this origin.
                  </div>
                </div>
                {!originServingCarrierUpdated && (
                  <div className="fix-issue-button-row">
                    <Button
                      id="fix-issue-button"
                      variant="contained"
                      size="medium"
                      color="primary"
                      endIcon={<FixArrow />}
                      onClick={() => {
                        openInsightsPanel(selectedOrigin.id, "Logistics", true);
                      }}
                    >
                      Fix Issue: Add Serving Carrier
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        {/* Destination Product */}
        {!validation?.destinationCanStoreProduct && (
          <div className="task-container">
            <div
              style={{
                height: destinationProductUpdated ? "140px" : "200px",
                boxShadow: destinationProductUpdated
                  ? "-2px 0px 0px #009F5C"
                  : "-2px 0px 0px #FF0000",
              }}
              className="task-box"
            >
              <div className="task-title-row">
                <img
                  src={
                    destinationProductUpdated
                      ? icons.TaskCompleted
                      : icons.TodoMarker
                  }
                  alt="Todo Marker"
                />
                <span className="task-title">
                  Add {product} to Destination&apos;s Product List
                </span>
              </div>
              <div className="task-details-row">
                <div className="task-details-first-point">
                  1. This destination has to have this product added to its
                  product list for the origin to be added.
                </div>
                <div className="task-details-second-point">
                  2. Locate the occupant and update the product list associated
                  with this destination.
                </div>
              </div>
              {!destinationProductUpdated && (
                <div className="fix-issue-button-row">
                  <Button
                    id="fix-issue-button"
                    variant="contained"
                    size="medium"
                    color="primary"
                    endIcon={<FixArrow />}
                    onClick={() => {
                      openInsightsPanel(
                        selectedLocation?.id || "",
                        "Occupants",
                        true
                      );
                    }}
                  >
                    Fix Issue: Add Product
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Destination Mode */}
        {!validation?.hasInboundMode && (
          <div className="task-container">
            <div
              style={{
                height: destinationModeUpdated ? "140px" : "200px",
                boxShadow: destinationModeUpdated
                  ? "-2px 0px 0px #009F5C"
                  : "-2px 0px 0px #FF0000",
              }}
              className="task-box"
            >
              <div className="task-title-row">
                <img
                  src={
                    destinationModeUpdated
                      ? icons.TaskCompleted
                      : icons.TodoMarker
                  }
                  alt="Todo Marker"
                />
                <span className="task-title">
                  Add Inbound {mode} to Destination
                </span>
              </div>
              <div className="task-details-row">
                <div className="task-details-first-point">
                  1. Inbound {selectedMode.toLocaleLowerCase()} has to be added
                  as a capability for the origin to be added.
                </div>
                <div className="task-details-second-point">
                  2. Select the checkbox labeled “Inbound” for this destination.
                </div>
              </div>
              {!destinationModeUpdated && (
                <div className="fix-issue-button-row">
                  <Button
                    id="fix-issue-button"
                    variant="contained"
                    size="medium"
                    color="primary"
                    endIcon={<FixArrow />}
                    onClick={() => {
                      openInsightsPanel(
                        selectedLocation?.id || "",
                        "Logistics",
                        true
                      );
                    }}
                  >
                    Fix Issue: Add Inbound {mode}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Destination Serving Carrier */}
        {selectedMode.toLowerCase() === "rail" &&
          validation.hasRailRouteToDestination &&
          !validation.hasRailRouteToServingDestinationCarrier && (
            <div className="task-container">
              <div
                style={{
                  height: destinationServingCarrierUpdated ? "140px" : "200px",
                  boxShadow: destinationServingCarrierUpdated
                    ? "-2px 0px 0px #009F5C"
                    : "-2px 0px 0px #FF0000",
                }}
                className="task-box"
              >
                <div className="task-title-row">
                  <img
                    src={
                      destinationServingCarrierUpdated
                        ? icons.TaskCompleted
                        : icons.TodoMarker
                    }
                    alt="Todo Marker"
                  />
                  <span className="task-title">
                    Add Missing Serving Carrier to Destination
                  </span>
                </div>
                <div className="task-details-row">
                  <div className="task-details-first-point">
                    1. This destination has to have a serving carrier selected
                    for the origin to be added.
                  </div>
                  <div className="task-details-second-point">
                    2. Add a carrier to the “Serving and Connecting Carriers”
                    section associated with this destination.
                  </div>
                </div>
                {!destinationServingCarrierUpdated && (
                  <div className="fix-issue-button-row">
                    <Button
                      id="fix-issue-button"
                      variant="contained"
                      size="medium"
                      color="primary"
                      endIcon={<FixArrow />}
                      onClick={() => {
                        openInsightsPanel(
                          selectedLocation?.id || "",
                          "Logistics",
                          true
                        );
                      }}
                    >
                      Fix Issue: Add Serving Carrier
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
      {/* Complete Button */}
      <div className="complete-button-container">
        <div
          style={{
            boxShadow: tasksFinished
              ? ""
              : "0px -2px 7.6px rgba(0, 0, 0, 0.25)",
          }}
          className="complete-button-border"
        />
        <Button
          id="complete-button"
          variant="contained"
          size="small"
          color="primary"
          disabled={!tasksFinished}
          style={{
            width: "100%",
            height: "40px",
            fontSize: "16px",
            fontWeight: 500,
          }}
          onClick={onComplete}
        >
          Complete
        </Button>
      </div>
    </div>
  );
};

export default AddMissingOrigin;
